// Flash Cards mock data structure for systematic organization
export const flashCardLanguages = [
  { id: 'en', name: 'English', code: 'en-US' },
  { id: 'zh-tw', name: 'Traditional Chinese', code: 'zh-TW' },
  { id: 'zh-cn', name: 'Simplified Chinese', code: 'zh-CN' },
  { id: 'es', name: 'Spanish', code: 'es-ES' },
  { id: 'fr', name: 'French', code: 'fr-FR' },
  { id: 'de', name: 'German', code: 'de-DE' },
  { id: 'ja', name: 'Japanese', code: 'ja-JP' },
  { id: 'ko', name: 'Korean', code: 'ko-KR' }
];

export const flashCardDifficulties = [
  { id: 'beginner', name: 'Beginner', description: 'Basic concepts and terminology' },
  { id: 'intermediate', name: 'Intermediate', description: 'Standard educational level' },
  { id: 'advanced', name: 'Advanced', description: 'Complex concepts and analysis' },
  { id: 'expert', name: 'Expert', description: 'Professional and specialized knowledge' }
];

export const flashCardCategories = [
  { id: 'science', name: 'Science', icon: '🔬', color: 'bg-blue-100 text-blue-800' },
  { id: 'history', name: 'History', icon: '📚', color: 'bg-amber-100 text-amber-800' },
  { id: 'geography', name: 'Geography', icon: '🌍', color: 'bg-green-100 text-green-800' },
  { id: 'language', name: 'Language', icon: '🗣️', color: 'bg-purple-100 text-purple-800' },
  { id: 'mathematics', name: 'Mathematics', icon: '🔢', color: 'bg-red-100 text-red-800' },
  { id: 'literature', name: 'Literature', icon: '📖', color: 'bg-indigo-100 text-indigo-800' },
  { id: 'arts', name: 'Arts', icon: '🎨', color: 'bg-pink-100 text-pink-800' },
  { id: 'technology', name: 'Technology', icon: '💻', color: 'bg-cyan-100 text-cyan-800' },
  { id: 'biology', name: 'Biology', icon: '🧬', color: 'bg-emerald-100 text-emerald-800' },
  { id: 'general', name: 'General Knowledge', icon: '🧠', color: 'bg-gray-100 text-gray-800' }
];

// Sample flash card sets for demonstration
export const sampleFlashCardSets = [
  {
    id: 'biology-basics-en',
    title: 'Biology Basics',
    language: 'English',
    difficulty: 'intermediate',
    category: 'biology',
    totalCards: 5,
    description: 'Fundamental concepts in biology',
    createdAt: '2024-01-15T10:00:00Z',
    cards: [
      {
        id: 1,
        question: 'What are the key building blocks of the chemicals found in living things?',
        answer: 'Carbon, hydrogen, oxygen, nitrogen, phosphorus, and sulfur',
        explanation: 'These six elements form the basis of all biological molecules including proteins, carbohydrates, lipids, and nucleic acids.',
        category: 'biochemistry',
        keywords: ['elements', 'biology', 'chemistry', 'life'],
        imageQuery: 'molecular structure biology',
        imageUrl: 'https://source.unsplash.com/800x600/?molecular,biology,chemistry',
        imageAlt: 'Molecular structure illustration'
      },
      {
        id: 2,
        question: 'What is the basic unit of life?',
        answer: 'The cell',
        explanation: 'All living organisms are composed of one or more cells, making the cell the fundamental unit of life.',
        category: 'cell biology',
        keywords: ['cell', 'life', 'organism', 'basic unit'],
        imageQuery: 'cell biology microscope',
        imageUrl: 'https://source.unsplash.com/800x600/?cell,microscope,biology',
        imageAlt: 'Cell under microscope'
      },
      {
        id: 3,
        question: 'What process do plants use to convert sunlight into energy?',
        answer: 'Photosynthesis',
        explanation: 'Photosynthesis is the process by which plants convert light energy into chemical energy (glucose) using carbon dioxide and water.',
        category: 'plant biology',
        keywords: ['photosynthesis', 'plants', 'sunlight', 'energy'],
        imageQuery: 'photosynthesis plants sunlight',
        imageUrl: 'https://source.unsplash.com/800x600/?photosynthesis,plants,sunlight',
        imageAlt: 'Plant photosynthesis process'
      },
      {
        id: 4,
        question: 'What carries genetic information in living organisms?',
        answer: 'DNA (Deoxyribonucleic acid)',
        explanation: 'DNA contains the genetic instructions needed for the development and function of all living organisms.',
        category: 'genetics',
        keywords: ['DNA', 'genetics', 'heredity', 'information'],
        imageQuery: 'DNA double helix genetics',
        imageUrl: 'https://source.unsplash.com/800x600/?DNA,genetics,helix',
        imageAlt: 'DNA double helix structure'
      },
      {
        id: 5,
        question: 'What is the process of cell division called?',
        answer: 'Mitosis (for somatic cells) or Meiosis (for reproductive cells)',
        explanation: 'Mitosis produces identical diploid cells for growth and repair, while meiosis produces genetically diverse haploid gametes for reproduction.',
        category: 'cell biology',
        keywords: ['mitosis', 'meiosis', 'cell division', 'reproduction'],
        imageQuery: 'cell division mitosis biology',
        imageUrl: 'https://source.unsplash.com/800x600/?cell,division,biology',
        imageAlt: 'Cell division process'
      }
    ]
  },
  {
    id: 'europe-geography-zh',
    title: 'European Geography',
    language: 'Traditional Chinese',
    difficulty: 'beginner',
    category: 'geography',
    totalCards: 4,
    description: 'Basic facts about European countries and capitals',
    createdAt: '2024-01-16T14:30:00Z',
    cards: [
      {
        id: 1,
        question: '法國的首都是什麼？',
        answer: '巴黎',
        explanation: '巴黎是法國的首都和最大城市，也是歐洲重要的文化和經濟中心。',
        category: 'capitals',
        keywords: ['法國', '巴黎', '首都', '歐洲'],
        imageQuery: 'Paris France Eiffel Tower',
        imageUrl: 'https://source.unsplash.com/800x600/?paris,france,eiffel',
        imageAlt: 'Paris cityscape with Eiffel Tower'
      },
      {
        id: 2,
        question: '意大利的形狀像什麼？',
        answer: '靴子',
        explanation: '意大利半島的形狀很像一隻靴子，這是地理學習中的經典記憶方法。',
        category: 'geography',
        keywords: ['意大利', '靴子', '半島', '形狀'],
        imageQuery: 'Italy map boot shape',
        imageUrl: 'https://source.unsplash.com/800x600/?italy,map,geography',
        imageAlt: 'Map of Italy showing boot shape'
      },
      {
        id: 3,
        question: '哪個國家被稱為「千湖之國」？',
        answer: '芬蘭',
        explanation: '芬蘭有超過18萬個湖泊，因此被稱為「千湖之國」。',
        category: 'nicknames',
        keywords: ['芬蘭', '千湖', '湖泊', '北歐'],
        imageQuery: 'Finland lakes nature',
        imageUrl: 'https://source.unsplash.com/800x600/?finland,lakes,nature',
        imageAlt: 'Finnish lakes landscape'
      },
      {
        id: 4,
        question: '歐洲最長的河流是什麼？',
        answer: '伏爾加河',
        explanation: '伏爾加河全長3530公里，是歐洲最長的河流，流經俄羅斯。',
        category: 'rivers',
        keywords: ['伏爾加河', '河流', '俄羅斯', '歐洲'],
        imageQuery: 'Volga river Russia',
        imageUrl: 'https://source.unsplash.com/800x600/?volga,river,russia',
        imageAlt: 'Volga River landscape'
      }
    ]
  }
];

// Flash card settings and preferences
export const flashCardSettings = {
  autoAdvanceDelay: 3000, // milliseconds
  defaultNumberOfCards: 5,
  maxNumberOfCards: 20,
  enableSound: true,
  enableAutoAdvance: true,
  showExplanations: true,
  shuffleCards: false
};

// Helper functions
export const getLanguageByCode = (code) => {
  return flashCardLanguages.find(lang => lang.code === code) || flashCardLanguages[0];
};

export const getDifficultyById = (id) => {
  return flashCardDifficulties.find(diff => diff.id === id) || flashCardDifficulties[1];
};

export const getCategoryById = (id) => {
  return flashCardCategories.find(cat => cat.id === id) || flashCardCategories[flashCardCategories.length - 1];
};

export const generateFlashCardId = (title, language, difficulty) => {
  const cleanTitle = title.toLowerCase().replace(/[^a-z0-9]/g, '-');
  const langCode = language.toLowerCase().replace(/[^a-z]/g, '');
  return `${cleanTitle}-${langCode}-${difficulty}`;
}; 